import React, {useEffect, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {motion} from 'framer-motion';
import {handleChange} from '../../utils/form';
import {isSmallHeight} from '../../utils/responsive';

const hologramMaskAnimation = keyframes`
    0% {
        mask-position:0% 130%;
        
    }
    100% {
        mask-position: 0% 200%;
    }
`;

const Container = styled.div`
  display: flex;
  position: absolute;
  width: ${({theme}) => theme.dimensions.wdp(100)}px;
  height: ${({theme}) => theme.dimensions.hdp(100)}px;
  overflow: hidden;
`;
const RobotContainer = styled(motion.div)<{formWidth: number}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 20%;
  left: 4%;
  object-fit: contain;
  width: ${({formWidth, theme}) =>
    `calc(${(theme.dimensions.wdp(100) - formWidth) / 2}px - ${isSmallHeight ? 5 : 8}%)`};
  aspect-ratio: 0.55;
  z-index: 2;
`;
const GenioRobotContainer = styled(motion.div)<{formWidth: number}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 20%;
  right: 4%;
  width: ${({formWidth, theme}) =>
    `calc(${(theme.dimensions.wdp(100) - formWidth) / 2}px - ${isSmallHeight ? 5 : 8}%)`};
  aspect-ratio: 0.55;
  z-index: 2;
`;
const FloatingContainer = styled(motion.div)`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
const RobotImage = styled.img`
  display: flex;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const RobotGenioImage = styled.img`
  display: flex;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const ArmRobotImage = styled(motion.img)`
  display: flex;
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 35% 30%;
`;
const FlameImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  bottom: 0;
  left: 0;
`;
const HologramContainer = styled.div`
  mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.7));
  mask-size: 100% 200%;
  mask-position: 0 100%;
  animation: ${hologramMaskAnimation} 4s ease-in-out infinite alternate;
  z-index: 1;
  display: flex;
  align-self: center;
  position: absolute;
  bottom: 18%;
  left: 50%;
  transform: translateX(-50%);
  width: 23rem;
  overflow: hidden;
`;
const Sphere = styled.img<{isVisible?: boolean; isLeft?: boolean}>`
  display: flex;
  width: 20%;
  aspect-ratio: 1;
  object-fit: scale-down;
  object-position: center;
  overflow: hidden;
  position: absolute;
  z-index: -1;

  opacity: ${({isVisible}) => (isVisible ? 1 : 0)};
  ${({isLeft}) =>
    isLeft
      ? css`
          right: 53%;
          top: 22%;
          transform: translateY(-100%) translateX(100%);
        `
      : css`
          left: 55%;
          top: 17%;
          transform: translateY(-100%) translateX(-100%);
        `}
`;

const ScrapeRobotAnimation: React.FC<any> = ({
  className,
  formWidth,
  setIsReady,
  isRobotSpeaking,
  isGenioRobotSpeaking,
}: {
  className: string;
  formWidth: number;
  setIsReady: (isReady: boolean) => void;
  isRobotSpeaking: boolean;
  isGenioRobotSpeaking: boolean;
}): JSX.Element => {
  const [isRobotFloating, setIsRobotFloating] = useState(false);
  const [isGenioRobotFloating, setIsGenioRobotFloating] = useState(false);
  const [startAnimation, setStartAnimation] = useState({
    image1: false,
    image2: false,
    image3: false,
    image4: false,
    image5: false,
  });

  const handleIsReady = (key: string) =>
    handleChange(key, true, setStartAnimation);
  return (
    <Container className={className}>
      <RobotContainer
        formWidth={formWidth}
        initial={{x: '-100vw', rotate: 30}}
        animate={
          startAnimation?.image1 && startAnimation?.image2
            ? {x: '0vw', rotate: 0}
            : {x: '-100vw', rotate: 30}
        }
        transition={{duration: 2}}
        onAnimationComplete={() => setIsRobotFloating(true)}>
        <FloatingContainer
          animate={isRobotFloating ? {y: [0, -30]} : {y: 0}}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatType: 'mirror',
            ease: 'easeInOut',
            delay: 1,
          }}>
          <RobotImage
            src={require('../../assets/images/scrapeRobot1.png')}
            onLoad={() => handleIsReady('image1')}
          />
          <Sphere
            isLeft
            isVisible={isRobotSpeaking}
            src={require('../../assets/images/sphere.gif')}
            alt={'genio ai'}
          />
          <FlameImage
            animate={{
              y: ['0%', '-1.5%'],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatType: 'mirror',
              ease: 'easeInOut',
            }}
            src={require('../../assets/images/flame.png')}
            onLoad={() => handleIsReady('image2')}
          />
        </FloatingContainer>
      </RobotContainer>
      {startAnimation?.image1 && startAnimation?.image2 && (
        <GenioRobotContainer
          formWidth={formWidth}
          initial={{x: '100vw', rotate: -20}}
          animate={
            startAnimation?.image3 && startAnimation?.image4
              ? {x: '0vw', rotate: 0}
              : {x: '100vw', rotate: -20}
          }
          transition={{duration: 2, delay: 1}}
          onAnimationComplete={(definition: any) => {
            setIsGenioRobotFloating(true);
            if (definition.x === '0vw') {
              setIsReady(true);
            }
          }}>
          <FloatingContainer
            animate={isGenioRobotFloating ? {y: [0, -30]} : {y: 0}}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: 'mirror',
              ease: 'easeInOut',
              delay: 1,
            }}>
            <RobotGenioImage
              src={require('../../assets/images/scrapeRobot3.png')}
              onLoad={() => handleIsReady('image3')}
            />
            <Sphere
              isVisible={isGenioRobotSpeaking}
              src={require('../../assets/images/sphere.gif')}
              alt={'genio ai'}
            />
            <ArmRobotImage
              animate={isGenioRobotFloating ? {rotateZ: [0, 14]} : {rotateZ: 0}}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: 'mirror',
                ease: 'linear',
              }}
              src={require('../../assets/images/scrapeRobotArm.png')}
              onLoad={() => handleIsReady('image4')}
            />
          </FloatingContainer>
        </GenioRobotContainer>
      )}
      <HologramContainer style={{opacity: startAnimation?.image5 ? 1 : 0}}>
        <RobotGenioImage
          src={require('../../assets/images/hologram.png')}
          onLoad={() => handleIsReady('image5')}
        />
      </HologramContainer>
    </Container>
  );
};

export default ScrapeRobotAnimation;
